import {
  FieldFunctionOptions,
  Reference,
  StoreObject,
  StoreValue,
  defaultDataIdFromObject,
} from '@apollo/client';

import {StorableInput} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  EmptyArgumentsError,
  ExistingObjectReference,
  TypenameOptionalGetter,
  getReferenceById,
} from 'graphql-client/typePolicies';

import {GridCellIndexesGraphQL} from '../../../graphql-fragments/inventory/__generated/GridCellIndexesGraphQL';

// TypePolicy Helpers for GridInterface and directStorable queries

/** Get a reference for Query.directStorable, which may request a GridInterface */
export function getDirectStorableReference(
  typename: TypenameOptionalGetter,
  {args, toReference}: FieldFunctionOptions,
  existing: ExistingObjectReference
): Reference | undefined {
  if (args == null) {
    throw new EmptyArgumentsError('getDirectStorableReference');
  }
  const input: StorableInput = args.input;
  return existing !== undefined
    ? existing
    : getReferenceById({typename, id: makeDirectStorableIdFromInput(input), toReference});
}

/** Get many references for Query.directStorables, which may request GridInterfaces */
export function getDirectStorableReferences(
  typename: TypenameOptionalGetter,
  {args, toReference, canRead}: FieldFunctionOptions,
  existing: ExistingObjectReference
): Reference | undefined {
  if (args == null) {
    throw new EmptyArgumentsError('getDirectStorableReferences');
  }

  if (existing !== undefined) {
    return existing;
  } else {
    const refs = args.inputs.map((input: StorableInput) =>
      getReferenceById({typename, id: makeDirectStorableIdFromInput(input), toReference})
    );
    return refs.every((ref: Reference) => canRead(ref)) ? refs : undefined;
  }
}

/** Returns a custom cacheId for a GridInterface
 *
 * Plates and Boxes have a "cell" field that contains information relevant to only a particular cell of a
 * grid, but may not actually be passed explicit arguments for which cell to look up at the field level.
 * This function detects grids with specific cell information and generates a cacheId that separates it from
 * other caches of the GridInterface.
 *
 * This should only be relevant for Query.directStorable and Query.directStorables queries for BoxNode and
 * Plate Node
 */
export function getGridInterfaceCacheId(grid: Readonly<StoreObject>): string | undefined {
  // if a specific cell's information is fetched, ensure that we cache it with an id that includes the row
  // and column index
  const {columnIndex, rowIndex} = (grid?.cell ?? {}) as GridCellIndexesGraphQL;
  const id = makeGridInterfaceCacheId(grid.id, rowIndex, columnIndex);
  return defaultDataIdFromObject({__typename: grid.__typename, id});
}

function makeDirectStorableIdFromInput(input: StorableInput): string | undefined {
  return makeGridInterfaceCacheId(input.id, input.rowIndex, input.colIndex);
}

function makeGridInterfaceCacheId(
  id: StoreValue,
  rowIndex: number | null | undefined,
  columnIndex: number | null | undefined
): string | undefined {
  if (id == null) {
    return undefined;
  }
  if (rowIndex == null || columnIndex == null) {
    return `${id}`;
  }
  return `${id}-${rowIndex}-${columnIndex}`;
}
